import { ageInYears, MIN_ACCOUNT_AGE } from '@mogzo/shared';
import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router';
import { type BirthMonth, BirthMonthField, toBirthMonth } from '../components/BirthMonthField';
import { SocialButtons } from '../components/SocialButtons';
import { captchaHeaders, Turnstile } from '../components/Turnstile';
import { UsernameField, type UsernameState } from '../components/UsernameField';
import { Alert, AuthLayout, Button, Field, LegalNotice, TextLink } from '../components/ui';
import { authClient, authErrorMessage, returnPath, siteUrl, withNext } from '../lib/auth-client';
import { usePublicConfig } from '../lib/public-config';

export function SignUp() {
  const config = usePublicConfig();
  const [params] = useSearchParams();
  const back = returnPath(params.get('next'));
  const [username, setUsername] = useState('');
  const [usernameState, setUsernameState] = useState<UsernameState>('empty');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birth, setBirth] = useState<BirthMonth>({ month: '', year: '' });
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tooYoung, setTooYoung] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const birthMonth = toBirthMonth(birth);
    if (!birthMonth) return setError('Choose your month and year of birth.');
    if (ageInYears(birthMonth) < MIN_ACCOUNT_AGE) return setTooYoung(true);
    if (usernameState !== 'free') return setError('Choose a username that’s free.');

    setBusy(true);
    const { error: failed } = await authClient.signUp.email({
      // better-auth's `name` is the username.
      name: username.trim(),
      email: email.trim(),
      password,
      birthMonth,
      callbackURL: siteUrl(withNext('/verify-email', back)),
      fetchOptions: { headers: captchaHeaders(captcha) },
    });
    setBusy(false);
    if (failed) {
      if (failed.code === 'AGE_RESTRICTED') return setTooYoung(true);
      setError(authErrorMessage(failed));
      setCaptchaReset((n) => n + 1);
      return;
    }
    setSentTo(email.trim());
  }

  if (tooYoung) {
    return (
      <AuthLayout title="You can still play">
        <p className="text-ink">
          You need to be at least {MIN_ACCOUNT_AGE} to create a MOGZO account. You can still play
          games without one.
        </p>
        <div className="mt-6">
          <TextLink to="/">Back to MOGZO</TextLink>
        </div>
      </AuthLayout>
    );
  }

  if (sentTo) {
    return (
      <AuthLayout title="Check your email">
        <p className="text-ink">
          We sent a link to <strong className="text-ink">{sentTo}</strong>. Open it to confirm your
          email and finish creating your account. The link works for 24 hours.
        </p>
        <p className="mt-4 text-sm text-muted">
          Nothing there? Check spam, or <TextLink to={withNext('/sign-in', back)}>sign in</TextLink>{' '}
          to get a new link.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Save your progress, join game communities and post your best runs."
      footer={
        <>
          Already have an account? <TextLink to={withNext('/sign-in', back)}>Sign in</TextLink>
        </>
      }
    >
      <SocialButtons providers={config?.socialProviders ?? []} next={back} />
      <form onSubmit={onSubmit} className="space-y-4" noValidate={false}>
        {error && <Alert>{error}</Alert>}
        <UsernameField value={username} onChange={setUsername} onState={setUsernameState} />
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          maxLength={128}
          hint="At least 8 characters. A short sentence is easy to remember and hard to guess."
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <BirthMonthField value={birth} onChange={setBirth} />
        <Turnstile
          siteKey={config?.turnstileSiteKey ?? null}
          onToken={setCaptcha}
          resetKey={captchaReset}
        />
        <Button busy={busy} disabled={!!config?.turnstileSiteKey && !captcha}>
          Create account
        </Button>
      </form>
      <LegalNotice action="creating an account" />
    </AuthLayout>
  );
}
